import { Component, computed, inject, signal, ViewChild } from '@angular/core';
import { FirstMessageType } from 'src/app/models/client-configuration';
import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Util } from '../../../util';
import { ConfigService } from 'src/app/services/config.service';
import { DialogComponent } from 'src/app/_shared/components/dialog/dialog.component';
import { passwordValidator } from 'src/app/_core/validators/password.validator';
import { confirmPasswordValidator } from 'src/app/_core/validators/confirmPassword.validator';

@Component({
  selector: 'app-terms-conditions-dialog',
  templateUrl: './terms-conditions-dialog.component.html',
  styleUrls: ['./terms-conditions-dialog.component.scss'],
})
export class TermsConditionsDialogComponent {

  private readonly apiService = inject(ApiService);
  private readonly authService = inject(AuthService);
  private readonly configService = inject(ConfigService);

  @ViewChild('dialog') dialog!: DialogComponent;

  readonly type = signal<FirstMessageType>(this.configService.get().firstLoginType);
  readonly termsUrl = signal<string>(this.configService.get().termsUrl);
  readonly requireChangePassword = signal<boolean>(this.configService.get().requireChangePasswordAfterFirstLogin);
  readonly requireChangeMail = signal<boolean>(this.configService.get().requireChangeMail);
  readonly showCheckBox = signal<boolean>(this.type() !== FirstMessageType.FIRSTLOGIN_MSG);
  readonly checked = signal<boolean>(false);

  readonly guidelinesText = signal<string>(this.configService.get().firstLoginMessage);
  readonly shouldShowGuidelinesText = computed(() =>
    !Util.isEmptyString(this.guidelinesText()) &&
    (this.type() === FirstMessageType.FIRSTLOGIN_MSG || this.type() === FirstMessageType.FIRSTLOGIN_MSG_AND_TERMS_COND));

  readonly title = computed(() => {
    if (!this.shouldShowGuidelinesText() && this.requireChangePassword()) {
      return 'login.reset.title';
    }

    if (!this.shouldShowGuidelinesText() && this.showCheckBox()) {
      return 'terms.title';
    }

    return 'general.information';
  });

  readonly changePasswordForm: FormGroup;
  readonly emailFormControl = new FormControl('', [Validators.required, Validators.email]);

  get newPasswordControl() {
    return this.changePasswordForm.get('newPassword');
  }

  get confirmNewPasswordControl() {
    return this.changePasswordForm.get('confirmNewPassword');
  }

  constructor() {
    const newPasswordControl = new FormControl('', [Validators.required, passwordValidator()]);
    const confirmNewPasswordControl = new FormControl('', [Validators.required, confirmPasswordValidator(newPasswordControl)]);

    this.changePasswordForm = new FormGroup({
      newPassword: newPasswordControl,
      confirmNewPassword: confirmNewPasswordControl,
    });

    this.newPasswordControl?.valueChanges.subscribe(() => {
      this.confirmNewPasswordControl?.updateValueAndValidity();
    });
  }

  accept() {
    const user = {
      ...this.authService.getLoggedUser(),
      termsAccepted: true,
    };

    if (this.requireChangePassword()) {
      if (!this.changePasswordForm.invalid) {
        user.newPassword = this.changePasswordForm.value.newPassword;
      } else {
        this.changePasswordForm.markAllAsTouched();
      }
    }

    if (this.requireChangeMail()) {
      if (!this.emailFormControl.invalid) {
        user.email = this.emailFormControl.value;
      } else {
        this.emailFormControl.markAllAsTouched();
      }
    }

    this.apiService.updateUser(user).subscribe((_user) => {
      this.authService.updateLoggedUser(_user);
      this.dialog.closeDialog();
    });
  }
}
